import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { doesListNameExistV2 } from '@app/lists/helpers/lists-checks-helpers';
import { ListsService } from '@app/lists/services/lists.service';
import { PlatformEnum } from '@panamax/app-state';
import { CameraSquareActions } from '@shared/components/camera-uploader-square/camera-square-actions.enum';
import { SquareClickedEvent } from '@shared/components/camera-uploader/square-click.interface';
import { ListTypes } from '@shared/constants/lists-constants';
import { AiListBuilderModalOptions, KeyValue } from '@usf/ngrx-list-ai';
import { v4 as uuid } from 'uuid';
import { AiListMetaDataService } from '@usf/ngrx-list-ai';
import { AIListBuilderService } from '@app/lists/services/ai-list-builder.service';
import { AIListSourceFile } from '@usf/list-ai-types';

@Component({
  selector: 'app-ai-list-builder-modal',
  templateUrl: './ai-list-builder-modal.component.html',
  styleUrls: ['./ai-list-builder-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiListBuilderModalComponent implements OnInit {
  @Input() platform: PlatformEnum;
  @Input() menuTypes: KeyValue[];
  @Input() listNameBlackList = new Set<string>();
  @Input() dismissPromise: Promise<string>;

  @Output() dismissed: EventEmitter<void> = new EventEmitter();
  @Output() submitted: EventEmitter<AiListBuilderModalOptions> =
    new EventEmitter();

  isBuildFromBestSellingProducts = false;
  readonly ListTypes = ListTypes;
  selectedListType = ListTypes.public;
  listNameIsValid = true;
  platformRef = PlatformEnum;
  listName = '';

  title: string;
  subtitle: string;
  pageNumber: number;
  selectedMenuType: string;
  cameraActions = CameraSquareActions;
  aiListPhotoError = '';
  selectedUploadType = 'Menus';

  selectedIngredientPreference = 'BOTH';

  constructor(
    readonly aiListMetaDataService: AiListMetaDataService,
    public aiListBuilderService: AIListBuilderService,
  ) {}

  ngOnInit() {
    this.setPageOneMetaData();
    if (this.dismissPromise) {
      this.dismissPromise.then(() => {
        this.aiListBuilderService.clearAiListPhotosAndErrors();
      });
    }
  }

  getAIListPhotos() {
    return this.aiListBuilderService.getAiListPhotos();
  }

  handleSquareClicked(clickedObject: SquareClickedEvent) {
    if (clickedObject.event == this.cameraActions.addPhoto) {
      this.aiListBuilderService.onAddAIListPhoto();
    } else if (clickedObject.event == this.cameraActions.deletePhoto) {
      this.aiListBuilderService.onDeletePhoto(clickedObject.index);
    }
  }

  next() {
    if (this.pageNumber === 1) {
      this.setPageTwoMetaData();
    } else if (this.pageNumber === 2) {
      this.setPageThreeMetaData();
    } else {
      this.onSubmit();
    }
  }

  setPageOneMetaData() {
    this.pageNumber = 1;
    this.title = 'i18n.lists.aiListBuilderModal.pageOneTitle';
    this.subtitle = 'i18n.lists.aiListBuilderModal.pageOneSubTitle';
  }

  setPageTwoMetaData() {
    this.pageNumber = 2;
    this.isBuildFromBestSellingProducts = false;
    this.title = 'i18n.lists.aiListBuilderModal.pageTwoTitle';
    this.subtitle = 'i18n.lists.aiListBuilderModal.pageTwoSubTitle';
  }

  setPageThreeMetaData() {
    this.pageNumber = 3;
    this.title = 'i18n.lists.aiListBuilderModal.pageThreeTitle';
    this.subtitle = 'i18n.lists.aiListBuilderModal.pageThreeSubTitle';
  }

  selectMenuType(menuType: string) {
    if (this.selectedMenuType === menuType) {
      this.selectedMenuType = '';
    } else {
      this.selectedMenuType = menuType;
    }
  }

  selectBuildFromBestSellingProducts() {
    this.setPageThreeMetaData();
    this.isBuildFromBestSellingProducts = true;
  }

  cancel() {
    if (this.pageNumber === 1) {
      this.dismissModal();
    } else if (this.pageNumber === 2) {
      this.setPageOneMetaData();
    } else {
      this.setPageTwoMetaData();
    }
  }

  checkListNameValidity() {
    this.listNameIsValid = !doesListNameExistV2(
      this.listName,
      this.listNameBlackList,
    );
  }

  dismissModal() {
    this.dismissed.emit();
    this.aiListBuilderService.clearAiListPhotosAndErrors();
  }

  onSubmit() {
    const sourceFiles: AIListSourceFile[] = this.getAIListPhotos().map(
      photo => ({
        fileName: photo.filepath,
        extension: photo.extension,
        s3FilePath: photo.filepath,
        inputType: 'MENU',
        parseType: this.formatIngredientPreference(
          this.selectedIngredientPreference,
        ),
      }),
    );

    this.submitted.emit({
      aiListId: this.createAIListId(),
      aiListName: this.listName,
      listType: this.selectedListType.toUpperCase(),
      isFromBestSellingProducts: this.isBuildFromBestSellingProducts,
      menuType: this.selectedMenuType,
      sourceFiles: sourceFiles,
      typeOfImages: this.selectedUploadType,
      ingredientPreference: this.formatIngredientPreference(
        this.selectedIngredientPreference,
      ),
    } as AiListBuilderModalOptions);
  }

  formatIngredientPreference(ingredientPreference: string) {
    return (
      ingredientPreference.charAt(0).toUpperCase() +
      ingredientPreference.slice(1).toLowerCase()
    );
  }

  createAIListId() {
    return uuid();
  }
}
